// The signed-in person's own settings (`/api/profile`, ProfileEndpoints). One setting today: the
// language CLMS writes to them in — the value both questionnaire mail paths resolve per recipient.
//
// Both DTOs come straight from the generated contract (F-2), because this surface DOES declare its
// response schema (`.Produces<MyLanguagePreferenceDto>()`), unlike the admin projections next door.
//
// There is no id in any of these calls. The endpoint acts on the caller's own row, so the client has no
// way to express "set somebody else's language" — which is the point, not an omission.

import type { components } from "@clms/contracts";
import { apiFetch } from "../lib/api";
import { SUPPORTED_LANGUAGES, type Language } from "../i18n";

/** The caller's stored preference: the tag, and who wrote it (`idp` mirror vs. `self`). */
export type LanguagePreference = components["schemas"]["MyLanguagePreferenceDto"];

/** Read the caller's own preference (mostly for a settings view; the switcher writes blind). */
export function getPreferredLanguage(signal?: AbortSignal): Promise<LanguagePreference> {
  return apiFetch<LanguagePreference>("/api/profile/language", { signal });
}

/**
 * Store the caller's own mail language, stamped `self` server-side so the request-time IdP mirror stands
 * aside for it. Passing `null` CLEARS the preference and hands the row back to the IdP / the
 * questionnaire's own language — a different request from "write to me in Finnish", deliberately.
 * An unsupported tag answers 422 rather than being coerced.
 *
 * `keepalive` because the UI change is instant and the write is not: a person who switches the language
 * and immediately reloads, follows a link or submits is the ordinary case, and a plain fetch dies with
 * the document it was started from — leaving the choice on screen (localStorage) and NOT on the row the
 * mail paths read, until the next load's reconciliation seeds the row from a browser default instead.
 * That is how a reload moments after a switch came back in the old language (task c4b87c3b).
 */
export function setPreferredLanguage(
  language: Language | null,
  signal?: AbortSignal,
): Promise<LanguagePreference> {
  return apiFetch<LanguagePreference>("/api/profile/language", {
    method: "PUT",
    body: { language },
    signal,
    keepalive: true,
  });
}

/**
 * What sign-in reconciliation settled: the language that now governs both the mail and the UI, and
 * whether this call is what put it on the row.
 */
export type LanguageReconciliation = components["schemas"]["LanguageReconciliationDto"];

/**
 * Settle the stored preference and the UI language against each other, at sign-in, in BOTH directions
 * (`POST /api/profile/language/reconcile`):
 *
 *   - the row has no preference → it is seeded from `language`, the one the person is actually reading
 *     CLMS in, so somebody who signs in and never touches the switcher stops being invisible to the mail
 *     paths;
 *   - the row HAS one → it comes back and the caller switches the UI to it, so a person whose preference
 *     is English stops reading a Finnish UI while their mail goes out in English.
 *
 * Idempotent: a stored preference is never overwritten by the SPA's current language, which may be no
 * more than a browser default.
 */
export function reconcilePreferredLanguage(
  language: Language | null,
  signal?: AbortSignal,
): Promise<LanguageReconciliation> {
  return apiFetch<LanguageReconciliation>("/api/profile/language/reconcile", {
    method: "POST",
    body: { language },
    signal,
  });
}

/** Narrow an i18next language string ("en-GB", "fi") to a tag this product stores, or null. */
export function toStorableLanguage(language: string): Language | null {
  const base = language.split("-")[0]?.toLowerCase();
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? (base as Language) : null;
}
